import { useState } from "react";
import { useDeployments } from "../../api/queries";
import type { Deployment } from "../../api/types";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/AsyncStates";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { QueryStates } from "../../components/QueryStates";
import { DeploymentStatusPill } from "../../components/StatePill";
import { DeploymentDetailModal } from "./DeploymentDetailModal";
import { formatRelativeTime, shortDigest } from "./deployFormat";
import { RollbackDialog } from "./RollbackDialog";
import "./services.css";

interface DeploymentTimelineProps {
  projectId: string;
  serviceId: string;
  serviceName: string;
  canManage: boolean;
}

/**
 * A service's deploy timeline (Host/Deploy UX Phase 2, #539) — replaces the
 * old static, inert deployment-history table (review wireframe C). Every
 * entry carries WHAT it shipped (image, short digest) alongside its status
 * and live converged/target counts, and is CLICKABLE through to a detail
 * view with the per-placement convergence breakdown and, from a past
 * successful entry, redeploy (see `DeploymentDetailModal`). Rollback is a
 * separate, direct per-row action (IA slice 4, ServiceRollout board) — the
 * ONE rollback surface (`RollbackDialog`, also opened from the service
 * header) with the real volume-risk preview, reached without opening the
 * detail modal first. The active (non-terminal) entry's `convergedCount`
 * polls live via `useDeployments`/`useDeployment`, so an operator watching a
 * rollout sees it
 * progress without refreshing.
 */
export function DeploymentTimeline({ projectId, serviceId, serviceName, canManage }: DeploymentTimelineProps) {
  const [selectedDeploymentId, setSelectedDeploymentId] = useState<string | null>(null);
  const [rollbackTarget, setRollbackTarget] = useState<Deployment | null>(null);
  const {
    data: deploymentsPage,
    isPending,
    isError,
    error,
    refetch,
  } = useDeployments(projectId, serviceId);

  const columns: DataTableColumn<Deployment>[] = [
    {
      key: "version",
      header: "Version",
      cell: (dep) => (
        <button
          type="button"
          className="data-table__primary-link deployment-timeline__version-button"
          onClick={() => setSelectedDeploymentId(dep.id)}
        >
          {dep.image || `Deployment ${dep.id}`}
        </button>
      ),
      className: "data-table__primary",
    },
    { key: "status", header: "Status", cell: (dep) => <DeploymentStatusPill status={dep.status} /> },
    {
      key: "digest",
      header: "Digest",
      cell: (dep) => (dep.digest ? shortDigest(dep.digest) : "Unpinned"),
      className: (dep) => (dep.digest ? "data-table__mono" : "data-table__empty-cell"),
    },
    {
      key: "converged",
      header: "Rolled out",
      cell: (dep) => `${dep.convergedCount} / ${dep.targetCount}`,
      className: "data-table__mono",
    },
    {
      key: "createdBy",
      header: "Triggered by",
      cell: (dep) => dep.createdBy || "—",
      className: "data-table__secondary",
    },
    {
      key: "createdAt",
      header: "Started",
      cell: (dep) => (
        <time dateTime={dep.createdAt} title={new Date(dep.createdAt).toLocaleString()}>
          {formatRelativeTime(dep.createdAt)}
        </time>
      ),
      className: "data-table__secondary",
    },
    // ServiceRollout board: a per-row rollback action on any past RUNNING attempt — the
    // same eligibility the server enforces (`requireDeploymentTarget`, ADR-0031 §2b), so
    // the button appears only where the confirm dialog would actually succeed. Role-gated
    // (S-8, UX only — the server holds the real decision) the same as every other manage
    // action on this page.
    ...(canManage
      ? [
          {
            key: "rollback",
            header: "",
            cell: (dep: Deployment) =>
              dep.status === "running" ? (
                <Button
                  variant="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRollbackTarget(dep);
                  }}
                >
                  Roll back to this
                </Button>
              ) : null,
            className: "deployment-timeline__rollback-cell",
          } satisfies DataTableColumn<Deployment>,
        ]
      : []),
  ];

  return (
    <section className="service-detail__section" aria-labelledby="service-history-heading">
      <h2 id="service-history-heading">Deployment timeline</h2>
      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading deployment timeline…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error ? error.message : "Couldn't load the deployment timeline. Check your connection and try again."
        }
        isEmpty={Boolean(deploymentsPage && deploymentsPage.items.length === 0)}
        empty={
          <EmptyState
            title="No deployments yet"
            description="Deploy this service above to see attempts here — successful and failed, each clickable for its full detail."
          />
        }
      >
        {deploymentsPage && (
          <DataTable
            columns={columns}
            rows={deploymentsPage.items}
            rowKey={(dep) => dep.id}
            onRowClick={(dep) => setSelectedDeploymentId(dep.id)}
          />
        )}
      </QueryStates>

      <DeploymentDetailModal
        projectId={projectId}
        serviceId={serviceId}
        deploymentId={selectedDeploymentId}
        canManage={canManage}
        onClose={() => setSelectedDeploymentId(null)}
      />

      <RollbackDialog
        projectId={projectId}
        serviceId={serviceId}
        serviceName={serviceName}
        target={rollbackTarget}
        fromDeployment={deploymentsPage?.items[0]}
        onClose={() => setRollbackTarget(null)}
      />
    </section>
  );
}
